import { useMemo } from "react"
import { useUserPermissions } from "./use-user-permissions"

/**
 * Permission string constants for all UI-reachable mutation actions.
 * Matches the backend permission reference in docs/roles-permissions.md.
 */
export const PERMISSIONS = {
  // Incidents
  INCIDENTS_ARCHIVE: "incidents.archive",
  INCIDENTS_DELETE: "incidents.delete",
  INCIDENTS_UPDATE: "incidents.update",
  INCIDENTS_STATUS_CHANGE: "incidents.status.change",
  INCIDENTS_ASSIGNEE_CHANGE: "incidents.assignee.change",
  INCIDENTS_RECORDABLE_CHANGE: "incidents.recordable.change",
  INCIDENTS_LOST_TIME_CHANGE: "incidents.lost_time.change",
  INCIDENTS_RESTRICTED_CHANGE: "incidents.restricted.change",
  INCIDENTS_MEDICAL_UPDATE: "incidents.medical.update",
  INCIDENTS_CHAT_SEND: "incidents.chat.send",

  // Inspections
  INSPECTIONS_ARCHIVE: "inspections.archive",
  INSPECTIONS_DELETE: "inspections.delete",
  INSPECTIONS_UPDATE: "inspections.update",
  INSPECTIONS_STATUS_CHANGE: "inspections.status.change",
  INSPECTIONS_ASSIGNEE_CHANGE: "inspections.assignee.change",

  // Issues
  ISSUES_ARCHIVE: "issues.archive",
  ISSUES_DELETE: "issues.delete",
  ISSUES_UPDATE: "issues.update",
  ISSUES_ASSIGNEE_CHANGE: "issues.assignee.change",
  INCIDENTS_FORMS_VIEW: "incidents.forms.view",
  INSPECTIONS_FORMS_VIEW: "inspections.forms.view",
  ISSUES_FORMS_VIEW: "issues.forms.view",

  // Reports
  REPORTS_ORGANIZATION_UPDATE: "reports.organization.update",

  // Forms
  FORMS_CREATE: "forms.create",
  FORMS_UPDATE: "forms.update",
  FORMS_DELETE: "forms.delete",

  // Form Responses
  FORM_RESPONSES_ATTACHMENT_ADD: "form_responses.attachment.add",
  FORM_RESPONSES_ATTACHMENT_DELETE: "form_responses.attachment.delete",
  FORM_RESPONSES_SIGNATURE_ADD: "form_responses.signature.add",
  FORM_RESPONSES_SIGNATURE_DELETE: "form_responses.signature.delete",

  // Custom Lists
  CUSTOM_LISTS_CREATE: "custom_lists.create",
  CUSTOM_LISTS_UPDATE: "custom_lists.update",
  CUSTOM_LISTS_DELETE: "custom_lists.delete",

  // Answer Sets
  ANSWER_SETS_CREATE: "answer_sets.create",

  // Groups
  GROUPS_CREATE: "groups.create",
  GROUPS_UPDATE: "groups.update",

  // Users
  USERS_INVITE: "users.invite",
  USERS_ADMIN_TOGGLE: "users.admin.toggle",
  USERS_ROLES_ASSIGN: "users.roles.assign",
  USERS_ROLES_REMOVE: "users.roles.remove",
  USERS_ENTITIES_UPDATE: "users.entities.update",
  USERS_GROUP_CHANGE: "users.group.change",
  USERS_STATUS_UPDATE: "users.status.update",

  // Entities
  ENTITIES_USERS_UPDATE: "entities.users.update",

  // Locations
  LOCATIONS_HOURS_UPLOAD: "locations.hours.upload",

  // Admin Pages
  ADMIN_USERS_VIEW: "admin.users.view",
  ADMIN_ORGANIZATIONS_VIEW: "admin.organizations.view",
  ADMIN_HOURS_VIEW: "admin.hours.view",
  ADMIN_ENTITIES_VIEW: "admin.entities.view",
} as const

export type PermissionString = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]

/**
 * Maps a permission string like "incidents.archive" to a human-readable domain label.
 */
export const PERMISSION_DOMAINS: Record<string, string> = {
  incidents: "Incidents",
  inspections: "Inspections",
  issues: "Issues",
  reports: "Reports",
  forms: "Forms",
  form_responses: "Form Responses",
  custom_lists: "Custom Lists",
  answer_sets: "Answer Sets",
  groups: "Groups",
  users: "Users",
  entities: "Entities",
  locations: "Locations",
  admin: "Admin Pages",
}

/**
 * Groups an array of permission strings by their domain prefix.
 * Returns entries sorted by the order defined in PERMISSION_DOMAINS.
 */
export function groupPermissionsByDomain(
  permissions: string[],
): { domain: string; label: string; permissions: string[] }[] {
  const groups: Record<string, string[]> = {}
  for (const p of permissions) {
    // Handle multi-segment prefixes like "form_responses" and "custom_lists"
    const domainKey =
      Object.keys(PERMISSION_DOMAINS).find((key) => p.startsWith(key + ".")) ?? p.split(".")[0]
    if (!groups[domainKey]) groups[domainKey] = []
    groups[domainKey].push(p)
  }
  const domainOrder = Object.keys(PERMISSION_DOMAINS)
  return Object.entries(groups)
    .sort(([a], [b]) => {
      const ai = domainOrder.indexOf(a)
      const bi = domainOrder.indexOf(b)
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi)
    })
    .map(([key, perms]) => ({
      domain: key,
      label: PERMISSION_DOMAINS[key] ?? key,
      permissions: perms,
    }))
}

export const PERMISSION_DESCRIPTIONS: Record<PermissionString, string> = {
  "incidents.archive": "Archive an incident report",
  "incidents.delete": "Delete an incident report without hard-deleting its stored data",
  "incidents.update": "Edit incident report details and fields",
  "incidents.status.change": "Change an incident status",
  "incidents.assignee.change": "Change an incident assignee",
  "incidents.recordable.change": "Toggle recordable flag on an incident",
  "incidents.lost_time.change": "Toggle lost time flag on an incident",
  "incidents.restricted.change": "Toggle restricted duty flag on an incident",
  "incidents.medical.update": "Update medical treatment fields for an incident",
  "incidents.chat.send": "Post a message in the incident chat",
  "inspections.archive": "Archive an inspection report",
  "inspections.delete": "Delete an inspection report without hard-deleting its stored data",
  "inspections.update": "Edit inspection report details and fields",
  "inspections.status.change": "Change an inspection status",
  "inspections.assignee.change": "Change an inspection assignee",
  "issues.archive": "Archive an issue report",
  "issues.delete": "Delete an issue report without hard-deleting its stored data",
  "issues.update": "Edit issue report details and fields",
  "issues.assignee.change": "Change an issue assignee",
  "incidents.forms.view": "View incident form templates",
  "inspections.forms.view": "View inspection form templates",
  "issues.forms.view": "View issue form templates",
  "reports.organization.update": "Change the reporting organization on an existing report",
  "forms.create": "Create new form templates",
  "forms.update": "Edit existing form templates",
  "forms.delete": "Delete form templates",
  "form_responses.attachment.add": "Add attachments to a form response",
  "form_responses.attachment.delete": "Delete attachments from a form response",
  "form_responses.signature.add": "Add signatures to a form response",
  "form_responses.signature.delete": "Delete signatures from a form response",
  "custom_lists.create": "Create custom list definitions",
  "custom_lists.update": "Edit custom list definitions",
  "custom_lists.delete": "Delete custom list definitions",
  "answer_sets.create": "Create answer sets for multiple-choice fields",
  "groups.create": "Create user groups",
  "groups.update": "Edit user groups",
  "users.invite": "Invite new users",
  "users.admin.toggle": "Grant or revoke admin visibility in the UI",
  "users.roles.assign": "Assign a role (permission pack) to a user",
  "users.roles.remove": "Remove a role from a user",
  "users.entities.update": "Update a user's entity assignments",
  "users.group.change": "Change a user's group assignment",
  "users.status.update": "Activate or deactivate a user account",
  "entities.users.update": "Update entity-to-user assignments",
  "locations.hours.upload": "Upload labor hours in org management",
  "admin.users.view": "View the user management page",
  "admin.organizations.view": "View organization settings and details",
  "admin.hours.view": "View labor hours data",
  "admin.entities.view": "View user-to-entity assignments",
}

/**
 * Higher-level permission helper hook.
 *
 * Returns readable boolean checks grouped by domain,
 * e.g. `canIncidents.update`, `canForms.create`.
 *
 * Uses useUserPermissions() internally — no additional fetches.
 */
export function usePermissions() {
  const { hasPermission, permissions, roles, isLoading, error, accessDenied, ssoEnabled } =
    useUserPermissions()

  const can = useMemo(() => {
    const check = (p: PermissionString) => permissions.includes(p)

    return {
      incidents: {
        archive: check(PERMISSIONS.INCIDENTS_ARCHIVE),
        delete: check(PERMISSIONS.INCIDENTS_DELETE),
        update: check(PERMISSIONS.INCIDENTS_UPDATE),
        statusChange: check(PERMISSIONS.INCIDENTS_STATUS_CHANGE),
        assigneeChange: check(PERMISSIONS.INCIDENTS_ASSIGNEE_CHANGE),
        recordableChange: check(PERMISSIONS.INCIDENTS_RECORDABLE_CHANGE),
        lostTimeChange: check(PERMISSIONS.INCIDENTS_LOST_TIME_CHANGE),
        restrictedChange: check(PERMISSIONS.INCIDENTS_RESTRICTED_CHANGE),
        medicalUpdate: check(PERMISSIONS.INCIDENTS_MEDICAL_UPDATE),
        chatSend: check(PERMISSIONS.INCIDENTS_CHAT_SEND),
        formsView: check(PERMISSIONS.INCIDENTS_FORMS_VIEW),
      },
      inspections: {
        archive: check(PERMISSIONS.INSPECTIONS_ARCHIVE),
        delete: check(PERMISSIONS.INSPECTIONS_DELETE),
        update: check(PERMISSIONS.INSPECTIONS_UPDATE),
        statusChange: check(PERMISSIONS.INSPECTIONS_STATUS_CHANGE),
        assigneeChange: check(PERMISSIONS.INSPECTIONS_ASSIGNEE_CHANGE),
        formsView: check(PERMISSIONS.INSPECTIONS_FORMS_VIEW),
      },
      issues: {
        archive: check(PERMISSIONS.ISSUES_ARCHIVE),
        delete: check(PERMISSIONS.ISSUES_DELETE),
        update: check(PERMISSIONS.ISSUES_UPDATE),
        assigneeChange: check(PERMISSIONS.ISSUES_ASSIGNEE_CHANGE),
        formsView: check(PERMISSIONS.ISSUES_FORMS_VIEW),
      },
      reports: {
        organizationUpdate: check(PERMISSIONS.REPORTS_ORGANIZATION_UPDATE),
      },
      forms: {
        create: check(PERMISSIONS.FORMS_CREATE),
        update: check(PERMISSIONS.FORMS_UPDATE),
        delete: check(PERMISSIONS.FORMS_DELETE),
      },
      formResponses: {
        attachmentAdd: check(PERMISSIONS.FORM_RESPONSES_ATTACHMENT_ADD),
        attachmentDelete: check(PERMISSIONS.FORM_RESPONSES_ATTACHMENT_DELETE),
        signatureAdd: check(PERMISSIONS.FORM_RESPONSES_SIGNATURE_ADD),
        signatureDelete: check(PERMISSIONS.FORM_RESPONSES_SIGNATURE_DELETE),
      },
      customLists: {
        create: check(PERMISSIONS.CUSTOM_LISTS_CREATE),
        update: check(PERMISSIONS.CUSTOM_LISTS_UPDATE),
        delete: check(PERMISSIONS.CUSTOM_LISTS_DELETE),
      },
      answerSets: {
        create: check(PERMISSIONS.ANSWER_SETS_CREATE),
      },
      groups: {
        create: check(PERMISSIONS.GROUPS_CREATE),
        update: check(PERMISSIONS.GROUPS_UPDATE),
      },
      users: {
        invite: check(PERMISSIONS.USERS_INVITE),
        adminToggle: check(PERMISSIONS.USERS_ADMIN_TOGGLE),
        rolesAssign: check(PERMISSIONS.USERS_ROLES_ASSIGN),
        rolesRemove: check(PERMISSIONS.USERS_ROLES_REMOVE),
        entitiesUpdate: check(PERMISSIONS.USERS_ENTITIES_UPDATE),
        groupChange: check(PERMISSIONS.USERS_GROUP_CHANGE),
        statusUpdate: check(PERMISSIONS.USERS_STATUS_UPDATE),
      },
      entities: {
        usersUpdate: check(PERMISSIONS.ENTITIES_USERS_UPDATE),
      },
      locations: {
        hoursUpload: check(PERMISSIONS.LOCATIONS_HOURS_UPLOAD),
      },
      admin: {
        usersView: check(PERMISSIONS.ADMIN_USERS_VIEW),
        organizationsView: check(PERMISSIONS.ADMIN_ORGANIZATIONS_VIEW),
        hoursView: check(PERMISSIONS.ADMIN_HOURS_VIEW),
        entitiesView: check(PERMISSIONS.ADMIN_ENTITIES_VIEW),
      },
    }
  }, [permissions])

  return {
    can,
    hasPermission,
    permissions,
    roles,
    isLoading,
    error,
    accessDenied,
    ssoEnabled,
  }
}
